import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Layers, Bot, Smartphone, TerminalSquare, MessageCircle, Rocket } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import projectsData from '../../assets/projects';

// 🔹 Import Simple Icons for stacks (React-Icons)
import {
  SiReact,
  SiNextdotjs,
  SiTypescript,
  SiTailwindcss,
  SiNodedotjs,
  SiCloudinary,
  SiJavascript,
  SiMysql,
  SiSqlite,
  SiFirebase,
  SiEjs,
  SiShadcnui,
  SiExpress,
  SiMongodb,
  SiPostgresql,
  SiDocker,
  SiGit,
  SiGithub,
  SiJsonwebtokens,
  SiVercel,
  SiFlask,
  SiPython,
  SiOpenai,
SiLangchain,
SiZapier,
SiN8N,
SiHubspot,
SiCalendly,
SiKotlin,
SiAndroidstudio,
SiCplusplus,
SiSalesforce,
} from 'react-icons/si';

// Category icons
const categoryIcons = {
  'Full-Stack Web Applications': <Layers className="w-4 h-4 inline mr-1" />,
  'AI & Automation': <Bot className="w-4 h-4 inline mr-1" />,
  'Mobile & Android Development': <Smartphone className="w-4 h-4 inline mr-1" />,
  'System / CLI Tools': <TerminalSquare className="w-4 h-4 inline mr-1" />,
  'Real-Time & Communication': <MessageCircle className="w-4 h-4 inline mr-1" />,
  'Open Source & Community': <Rocket className="w-4 h-4 inline mr-1" />,
  'CRM / Enterprise Tools': <Layers className="w-4 h-4 inline mr-1" />,
};

// 🔹 Map stack name → icon
const techIcons = {
  ReactJS: <SiReact className="text-cyan-400 w-4 h-4" />,
  NextJS: <SiNextdotjs className="text-gray-200 w-4 h-4" />,
  TypeScript: <SiTypescript className="text-blue-500 w-4 h-4" />,
  TailwindCSS: <SiTailwindcss className="text-sky-400 w-4 h-4" />,
  NodeJS: <SiNodedotjs className="text-green-500 w-4 h-4" />,
  Express: <SiExpress className="text-gray-300 w-4 h-4" />,
  MongoDB: <SiMongodb className="text-green-400 w-4 h-4" />,
  PostgreSQL: <SiPostgresql className="text-sky-500 w-4 h-4" />,
  Docker: <SiDocker className="text-blue-400 w-4 h-4" />,
  Git: <SiGit className="text-orange-500 w-4 h-4" />,
  GitHub: <SiGithub className="text-gray-200 w-4 h-4" />,
  Vercel: <SiVercel className="text-white w-4 h-4" />,
  JWT: <SiJsonwebtokens className="text-yellow-500 w-4 h-4" />,
  ShadCN : <SiShadcnui className="text-purple-400 w-4 h-4" />,
  Cloudinary: <SiCloudinary className="text-blue-300 w-4 h-4" />,
  Firebase: <SiFirebase className="text-yellow-400 w-4 h-4" />,
  JavaScript: <SiJavascript className="text-yellow-300 w-4 h-4" />,
  MySQL: <SiMysql className="text-blue-500 w-4 h-4" />,
  SQLite: <SiSqlite className="text-blue-400 w-4 h-4" />,
  Ejs : <SiEjs className="text-red-400 w-4 h-4" />,
  Flask: <SiFlask className="text-black w-4 h-4" />,
  Python: <SiPython className="text-yellow-400 w-4 h-4" />,
  OpenAI: <SiOpenai className="text-green-400 w-4 h-4" />,
LangChain: <SiLangchain className="text-blue-400 w-4 h-4" />,

  Zapier: <SiZapier className="text-red-400 w-4 h-4" />,
  n8n: <SiN8N className="text-black w-4 h-4" />,
  HubSpot: <SiHubspot className="text-orange-400 w-4 h-4" />,
  Calendly: <SiCalendly className="text-green-400 w-4 h-4" />,
Kotlin: <SiKotlin className="text-purple-400 w-4 h-4" />,
"Android Studio": <SiAndroidstudio className="text-green-400 w-4 h-4" />,
"C++" : <SiCplusplus className="text-blue-400 w-4 h-4" />,
Salesforce  : <SiSalesforce className="text-blue-500 w-4 h-4" />,
};

// 🔹 Capsule with icon + label
const Capsule = ({ label }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm shadow-sm transition hover:shadow-lg hover:scale-[1.03]">
    {techIcons[label] && <span>{techIcons[label]}</span>}
    <span className="text-xs font-medium text-cyan-300">{label}</span>
  </div>
);

const Project = () => {
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const [activeCategory, setActiveCategory] = useState(projectsData[0].category);
  const categories = projectsData.map((cat) => cat.category);
  const selectedCategory = projectsData.find((cat) => cat.category === activeCategory);

  return (
    <section id="projects" className="text-white py-16 px-4 sm:px-6 md:px-10 lg:px-20">
      <h2 className="text-4xl font-bold text-center mb-12">Projects</h2>

      {/* Category Buttons */}
      <div className="flex flex-wrap justify-center gap-4 mb-10">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 border flex items-center gap-1 backdrop-blur-md shadow-md bg-gradient-to-br from-[#0f1f2e]/60 via-[#102e42]/60 to-[#0e1d2c]/60 backdrop-blur-xl ${
              activeCategory === category
                ? 'bg-cyan-400 text-black border-cyan-500'
                : 'bg-white/10 text-cyan-300 border-white/20 hover:bg-white/20'
            }`}
          >
            {categoryIcons[category]}
            {category}
          </button>
        ))}
      </div>

      {/* Projects Grid */}
      <div className="space-y-8">
        <h3 className="text-2xl font-semibold mb-6 border-b border-white/10 pb-2 text-cyan-300 text-center">
          {selectedCategory.category}
        </h3>
        <div className="grid gap-8 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {selectedCategory.projects.map((project, index) => (
            <div
              key={index}
              data-aos="fade-up"
              className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 shadow-2xl hover:shadow-cyan-500/10 hover:scale-[1.025] transition-transform duration-300 flex flex-col justify-between bg-gradient-to-br from-[#0f1f2e]/60 via-[#102e42]/60 to-[#0e1d2c]/60 backdrop-blur-xl"
            >
              <div className="mb-4">
                <h4 className="text-xl font-semibold text-cyan-400 mb-2">{project.title}</h4>
                <p className="text-gray-300 text-sm leading-relaxed mb-3">
                  {project.description.length > 200
                    ? `${project.description.slice(0, 200)}...`
                    : project.description}
                </p>

                {/* 🔹 Tech Stack Capsules */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.stack.map((tech, i) => (
                    <Capsule key={i} label={tech} />
                  ))}
                </div>
              </div>

              {/* Links */}
              <div className="flex items-center justify-between mt-auto w-full border-t border-white/5 pt-4">
                <Link
                  to={`/projects/${project.projectSlug}`}
                  className="text-cyan-300 text-sm font-semibold flex items-center gap-1 hover:text-cyan-400 hover:underline transition-all cursor-pointer"
                >
                  View Case Study <ArrowUpRight size={14} />
                </Link>
                {project.liveLink && (
                  <a
                    href={project.liveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 text-xs flex items-center gap-0.5 hover:text-white transition-colors"
                  >
                    Live Link
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Project;
